// Shared-sprite icons for the UI (card headers, search results, legends), so
// they match the map markers exactly. The map (Mapbox GL) renders these same
// icons from the `clon8nsu…/draft` sprite referenced by the wrapper styles;
// here we crop the same committed sprite sheet for HTML/SVG use. Keep this in
// sync with the sprite the map style points at (see public/map-styles/*.json).

// Served same-origin from the widget's own deploy.
export const SPRITE_URL = '/map-sprite/sprite-2x.png';

// Natural pixel dimensions of the @2x sprite sheet.
export const SPRITE_SHEET = { width: 626, height: 492 };

export type SpriteRect = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

// Icon rects within the sheet (from the draft sprite @2x JSON).
const SPRITE_RECTS = {
  water: { x: 574, y: 0, width: 52, height: 52 },
  poi_boatramp: { x: 246, y: 358, width: 48, height: 48 },
  poi_marina: { x: 438, y: 358, width: 48, height: 48 },
  poi_baitshop: { x: 52, y: 358, width: 50, height: 50 },
  poi_charter: { x: 294, y: 358, width: 48, height: 48 },
  poi_gear: { x: 390, y: 358, width: 48, height: 48 },
  poi_flyfishing: { x: 342, y: 358, width: 48, height: 48 },
  poi_refuge: { x: 150, y: 358, width: 48, height: 50 },
  poi_multiple: { x: 102, y: 358, width: 48, height: 50 },
} as const;

// Maps the widget's UI icon names (the old SVGSprite ids) to shared-sprite
// icons. Fallbacks mirror the map's icon-image fallbacks in src/map/layers.ts:
// the draft sprite has no license-vendor, national-park, or top-place ("bow-bp")
// variant yet, so those reuse the nearest existing icon — keeping card and map
// identical. When those icons land in the sprite, update both here and layers.ts.
const UI_TO_SPRITE = new Map<string, keyof typeof SPRITE_RECTS>([
  ['bow', 'water'],
  ['bow-bp', 'water'], // FALLBACK: no top-place variant yet
  ['ramp', 'poi_boatramp'],
  ['marina', 'poi_marina'],
  ['bait-shop', 'poi_baitshop'],
  ['charter', 'poi_charter'],
  ['gear-and-equipment', 'poi_gear'],
  ['fly-fishing-shop', 'poi_flyfishing'],
  ['refuge', 'poi_refuge'],
  ['national-park', 'poi_refuge'], // FALLBACK: no national-park icon yet
  ['license-vendor', 'poi_gear'], // FALLBACK: no license-vendor icon yet
  ['multiple', 'poi_multiple'],
]);

// Returns the sprite rect for a UI icon name, or null if it has no shared-sprite
// equivalent (e.g. mpa, place, and pure-UI glyphs) — those stay on the SVG sprite.
export const spriteRectForIcon = (icon: string): SpriteRect | null => {
  const spriteName = UI_TO_SPRITE.get(icon);
  return spriteName ? SPRITE_RECTS[spriteName] : null;
};
